// import AuthCode from 'react-auth-code-input';
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material"
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { HiOutlineSearch } from "react-icons/hi";
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

import BaseIconInput from "../BaseIconInput";

import { useMutation } from "@apollo/client";
import { UserSetMedications } from "../../apollo-client/api-query";
import { showErrorToast } from "../../utils/toast";
import { refetchMe } from "../../store";

/**
 * @param {Object} props
 */
const MedicationSelectForm = ({ 
  isLabel, 
  step = 2, 
  setShowMedicationSearchInput,
  currentMedications,
  setCurrentMedications
}) => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const [setMedications] = useMutation(UserSetMedications, {fetchPolicy: 'network-only'})

  const [medicationList, setMedicationList] = useState([])
  const [strengthList, setStrengthList] = useState([])
  const [selectedStep, setSelectedStep] = useState(0)
  const [searchKey, setSearchKey] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [hoveredMedication, setHoveredMedication] = useState('')
  const [clickedMedication, setClickedMedication] = useState('')
  const [selectedMedications, setSelectedMedications] = useState([])
  const [selectedMedication, setSelectedMedication] = useState('')

  useEffect(() => {
    if (searchKey) {
      getMedications(searchKey)
    }
  }, [searchKey])

  const getMedications = async (searchKey) => {
    const response = await axios.get(`https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search?ef=STRENGTHS_AND_FORMS&authenticity_token=&maxList=&terms=${searchKey}`)

    if (response.data) {
      setMedicationList(response?.data?.[1])
      setStrengthList(response?.data?.[2]?.STRENGTHS_AND_FORMS?.[0] || [])
    }
  }

  const handleChangeInput = async (e) => {
    const value = e.target.value
    setSearchKey(value)
    setShowDropdown(value ? true : false)
    setSelectedStep(0)
  }

  const handleResetInput = () => {
    setSearchKey('')
  }

  const handleClickAddMedication = (item) => {
    setSelectedMedication(item)
    setSearchKey(item)
    setSelectedStep(1)
  }

  const handleClickAddStrength = (item) => {
    setSearchKey('')
    const currentItems = currentMedications.map(cm => ({ name: cm.name, strength: cm.strength }))
    const medications = [...selectedMedications, ...currentItems, {
      name: selectedMedication,
      strength: item
    }]
    
    if (step > 2) setSelectedStep(2)
    
    setCurrentMedications(medications)
    setSelectedMedications(medications)
    handleClickFinish(medications)
  }

  const handleClickRemoveMedication = (item) => {
    setSelectedMedications(selectedMedications.filter(m => m.name !== item.name))
  }

  const handleClickFinish = (medications) => {
    setShowDropdown(false)
    setShowMedicationSearchInput(false)
    setMedications({
      variables: {
        userId: id,
        medications
      }
    }).catch(error => {
      showErrorToast({
        message: error.message
      })
    }).then(({ data }) => {
      dispatch(refetchMe(true))
    })
  }

  return (
    <Box>
      <BaseIconInput
        height='48'
        label={isLabel ? 'Medication name' : ''}
        placeholder='Enter medication name'
        startIcon={<HiOutlineSearch size={20} />}
        endIcon={<CloseIcon /> }
        value={searchKey}
        onChange={handleChangeInput}
        onClickEndIcon={handleResetInput}
        onFocus={() => setShowDropdown(true)}
        required
      />
      <Box
        sx={{
          minHeight: '260px',
          display: !showDropdown ? 'flex' : 'none',
          justifyContent: 'center',
          pt: '56px'
        }}
      >
        <Typography variant='subText' color="primary.darkGray">
          No medications added
        </Typography>
      </Box>
      <Box
        sx={{
          minHeight: '260px',
          display: showDropdown ? 'block' : 'none',
        }}
      >
        <Box
          sx={{
            display: selectedStep === 0 ? 'block' : 'none',
            mt: '16px',
            border: '1px solid',
            borderColor: 'primary.mediumGray',
            borderRadius: '8px',
            padding: '4px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: medicationList.length > 0 ? '260px' : 'auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '4px'
            }}
            className='notification-dropdown-scroll'
          >
            {medicationList.length > 0 && medicationList.map((item => (
              <Box
                key={item}
                sx={{
                  minHeight: '39px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  bgcolor: clickedMedication === item ? 'primary.lightBlue' : 'none',
                  cursor: 'pointer'
                }}
                onMouseOver={() => setHoveredMedication(item)}
                onMouseOut={() => setHoveredMedication('')}
                onClick={() => setClickedMedication(item)}
              >
                <Typography 
                  variant="body" 
                  color={clickedMedication === item ? 'primary.mainBlue' : hoveredMedication === item ? 'primary.black' : 'primary.darkGray'}
                  sx={{
                    maxWidth: '350px',
                    overflowWrap: 'break-word'
                  }}
                >
                  {item}
                </Typography>
                <Box
                  sx={{
                    // minWidth: '30px',
                    display: (hoveredMedication === item || clickedMedication === item) ? 'flex' : 'none',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'primary.mainBlue'
                  }}
                  onClick={() => handleClickAddMedication(item)}
                >
                  <Typography variant="subText" color='primary.mainBlue'>
                    Add
                  </Typography>
                  <ArrowForwardIosIcon
                    sx={{ 
                      fontSize: '10px' ,
                      color: 'primary.mainBlue',
                      ml: '2px',
                      mt: '1px',
                    }}
                  />
                </Box>
              </Box>
            )))}
            {medicationList.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    padding: '8px 16px'
                  }}
                >
                  <Typography variant="body" color='primary.darkGray'>
                    No result for {searchKey}
                  </Typography>
                </Box>
                {/* <Box
                  sx={{
                    padding: '12px 16px'
                  }}
                >
                  <Typography
                    variant="subText" 
                    color='primary.mainBlue'
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'primary.mainBlue',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleClickAddMedication(searchKey)}
                  >
                    Add anyway
                  </Typography>
                </Box> */}
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: selectedStep === 1 ? 'block' : 'none', 
            mt: '16px',
            border: '1px solid',
            borderColor: 'primary.mediumGray',
            borderRadius: '8px',
            paddingRight: '4px'
          }}
        >
          <Box
            sx={{
              padding: '0px 8px'
            }}
          >
            <Box
              sx={{
                borderBottom: '1px solid',
                borderColor: 'primary.lightGray',
                padding: '12px 4px',
                borderRadius: '8px'
              }}
            >
              <Typography variant="h4" color='primary.mainBlue'>
                Choose strength
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: '144px',
              overflowY: 'auto',
              padding: '8px 4px 8px 8px'
            }}
            className='notification-dropdown-scroll'
          >
            {strengthList.map((item => (
              <Box
                key={item}
                sx={{
                  minHeight: '39px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  bgcolor: clickedMedication === item ? 'primary.lightBlue' : 'none',
                  cursor: 'pointer'
                }}
                onClick={() => setClickedMedication(item)}
                onMouseOver={() => setHoveredMedication(item)}
                onMouseOut={() => setHoveredMedication('')}
              >
                <Typography
                  variant="body" 
                  color={clickedMedication === item ? 'primary.mainBlue' : hoveredMedication === item ? 'primary.black' : 'primary.darkGray'}
                >
                  {item}
                </Typography>
                <Box
                  sx={{
                    display: (hoveredMedication === item || clickedMedication === item) ? 'flex' : 'none',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'primary.mainBlue'
                  }}
                  onClick={() => handleClickAddStrength(item)}
                >
                  <Typography variant="subText" color='primary.mainBlue'>
                    Add
                  </Typography>
                  <ArrowForwardIosIcon
                    sx={{
                      fontSize: '10px' ,
                      color: 'primary.mainBlue',
                      ml: '2px',
                      mt: '1px',
                    }}
                  />
                </Box>
              </Box>
            )))}
          </Box>
          <Box
            sx={{
              padding: '0px 12px'
            }}
          >
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'primary.lightGray',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '8px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid',
                  borderColor: 'primary.mediumGray',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedStep(0)}
              >
                <ArrowBackIosIcon 
                  sx={{ 
                    fontSize: '10px' ,
                    color: 'primary.mediumGray',
                    ml: '2px',
                    mt: '1px',
                  }}
                />
                <Typography variant="subText" color='primary.mediumGray'>
                  Back to list
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid',
                  borderColor: 'primary.mainBlue',
                  cursor: 'pointer'
                }}
                onClick={() => handleClickAddStrength('n/a')}
              >
                <Typography variant="subText" color='primary.mainBlue'>
                  Skip Strength and add
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: selectedStep === 2 ? 'block' : 'none', 
            mt: '16px',
            border: '1px solid',
            borderColor: 'primary.lightGray',
            borderRadius: '8px',
            padding: '8px'
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.lightGray',
              padding: '4px 16px',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="subText" color='primary.black'>
              My MEDICATION
            </Typography>
            <Typography variant="subText" color='primary.black'>
              STRENGTH
            </Typography>
          </Box>
          <Box>
            {selectedMedications.map((item => (
              <Box
                key={item.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: '8px',
                  padding: '4px 8px 4px 0'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleClickRemoveMedication(item)}
                  >
                    <CloseIcon
                      sx={{
                        color: 'primary.mediumGray',
                        fontSize: '18px'
                      }}
                    />
                  </Box>
                  <Typography variant='subText' color='primary.black' maxWidth='240px'>
                    {item.name}
                  </Typography>
                </Box>
                <Typography variant='subText' color='primary.black' textAlign={'right'}>
                  {item.strength}
                </Typography>
              </Box>
            )))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default MedicationSelectForm;